import * as React from 'react';
import { signalItemsViewRenderComplete, signalItemsViewNavigationStart } from '../../operations/pageLoad';
import { IFacet } from '../../models/Facet';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { errorFacet } from '../../facets/DemandFacets';
import { Fragment } from '../../utilities/components/Fragment';
import { IItemsScopeStateProps } from '../itemsScope/ItemsScope';
import { IOnCurrentItemKeyChangedProps } from '../itemsNavigator/ItemsNavigator';
import { IDecoratedProps } from '../../utilities/components/Decorators';

/**
 * @internal
 */
export interface IPageLoadTrackerBaseProps {
    itemKey?: string | undefined;
    isReady: boolean;
    pageLoadTime: number | undefined;
    signalItemsViewRenderComplete: (pageLoadTime: number | undefined) => void;
    signalItemsViewNavigationStart: (payload: IOnCurrentItemKeyChangedProps) => void;
}

/**
 * @internal
 */
export interface IPageLoadTrackerBaseState {
    // Nothing.
}

/**
 * @internal
 */
export class PageLoadTrackerBase extends React.PureComponent<IPageLoadTrackerBaseProps, IPageLoadTrackerBaseState> {
    public render(): JSX.Element {
        return (
            <Fragment>{ this.props.children }</Fragment>
        );
    }

    public componentDidMount(): void {
        const {
            isReady,
            itemKey,
            pageLoadTime
        } = this.props;

        this.props.signalItemsViewNavigationStart({
            previousItemKey: undefined,
            currentItemKey: itemKey
        });

        if (isReady) {
            this.props.signalItemsViewRenderComplete(pageLoadTime);
        }
    }

    public componentDidUpdate(previousProps: IPageLoadTrackerBaseProps): void {
        const {
            isReady,
            itemKey,
            pageLoadTime
        } = this.props;

        if (itemKey !== previousProps.itemKey) {
            this.props.signalItemsViewNavigationStart({
                previousItemKey: previousProps.itemKey,
                currentItemKey: itemKey
            });
        }

        if ((!previousProps.isReady || itemKey !== previousProps.itemKey) && isReady) {
            this.props.signalItemsViewRenderComplete(pageLoadTime);
        }
    }
}

/**
 * @internal
 */
export type IPageLoadTrackerProps = IDecoratedProps<
    IPageLoadTrackerBaseProps,
    Pick<IPageLoadTrackerBaseProps, 'isReady' | 'signalItemsViewRenderComplete' | 'signalItemsViewNavigationStart'>,
    Partial<Pick<IPageLoadTrackerBaseProps, 'isReady'>> & {
        facets: IFacet<{}>[];
    }
    >;

interface IPageLoadTrackerInternalProps {
    itemKey?: string | undefined;
    facets: IFacet<{}>[];
    isReady?: boolean;
}

/**
 * @internal
 */
export const PageLoadTracker: React.ComponentType<IPageLoadTrackerProps> =
    connectToItemsScope()(connectToItemCache((
        itemCache: IItemCache,
        props: IPageLoadTrackerInternalProps & IItemsScopeStateProps
    ): Pick<IPageLoadTrackerBaseProps, 'isReady' | 'itemKey'> => {
        const {
            itemKey,
            isReady: parentIsReady = true
        } = props;

        const isReady = props.facets.every((facet: IFacet<{}>) => itemCache.isItemFacetResolved(facet, itemKey));
        const isError = itemCache.isItemFacetResolved(errorFacet, itemKey);

        return {
            isReady: parentIsReady && (isReady || isError),
            itemKey
        };
    }, (dispatch: IItemCacheDispatch, props: IPageLoadTrackerInternalProps & IItemsScopeStateProps) => {
        const {
            itemsScopeItemKey
        } = props;

        return {
            signalItemsViewRenderComplete: (pageLoadTime: number | undefined) => {
                dispatch(signalItemsViewRenderComplete(itemsScopeItemKey, pageLoadTime));
            },
            signalItemsViewNavigationStart: (payload: IOnCurrentItemKeyChangedProps) => {
                dispatch(signalItemsViewNavigationStart({
                    ...payload,
                    itemsScopeItemKey
                }));
            }
        };
    })(PageLoadTrackerBase)
    );
